import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadVideo } from './videoApi';

let seq = 0;

// Clips recorded during an assessment. Each is kept in memory (Blob) and
// uploaded one at a time as soon as a profile id is known; a failed upload
// stays in the list with a retry instead of being dropped.
// Clip: { key, blob, filename, title, durationSec, size, profileId,
//         status: 'queued' | 'uploading' | 'done' | 'error', progress, error }
export default function useClipQueue(profileId, { onUploaded } = {}) {
  const [clips, setClips] = useState([]);
  const [tick, setTick] = useState(0);
  const busyRef = useRef(false);
  const profileIdRef = useRef(profileId);
  const onUploadedRef = useRef(onUploaded);
  profileIdRef.current = profileId;
  onUploadedRef.current = onUploaded;

  const patch = useCallback((key, fields) => {
    setClips((prev) =>
      prev.map((c) => (c.key === key ? { ...c, ...fields } : c))
    );
  }, []);

  const add = useCallback(
    ({ blob, filename, title, durationSec, profileId: forProfileId }) => {
      seq += 1;
      const clip = {
        key: `clip-${Date.now()}-${seq}`,
        blob,
        filename,
        title: title || '',
        durationSec: durationSec || 0,
        size: blob.size,
        profileId: forProfileId || profileIdRef.current || null,
        status: 'queued',
        progress: 0,
        error: null,
      };
      setClips((prev) => [...prev, clip]);
      return clip.key;
    },
    []
  );

  const retry = useCallback(
    (key) => patch(key, { status: 'queued', progress: 0, error: null }),
    [patch]
  );

  const discard = useCallback(
    (key) => setClips((prev) => prev.filter((c) => c.key !== key)),
    []
  );

  useEffect(() => {
    if (busyRef.current) return;
    const next = clips.find(
      (c) => c.status === 'queued' && (c.profileId || profileId)
    );
    if (!next) return;
    const target = next.profileId || profileId;
    busyRef.current = true;
    patch(next.key, {
      status: 'uploading',
      progress: 0,
      error: null,
      profileId: target,
    });
    uploadVideo(target, next.blob, {
      title: next.title,
      durationSec: next.durationSec,
      filename: next.filename,
      onProgress: (p) => patch(next.key, { progress: p }),
    })
      .then((asset) => {
        patch(next.key, { status: 'done', progress: 1, blob: null });
        if (onUploadedRef.current) onUploadedRef.current(asset, target);
      })
      .catch((err) => {
        patch(next.key, {
          status: 'error',
          error: err.message || 'Ошибка загрузки',
        });
      })
      .finally(() => {
        busyRef.current = false;
        setTick((n) => n + 1);
      });
  }, [clips, profileId, tick, patch]);

  const unsent = clips.some((c) => c.status !== 'done');
  useEffect(() => {
    if (!unsent) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsent]);

  return { clips, add, retry, discard, unsent };
}
